import Image from "next/image";
import { Mail, MapPin } from "lucide-react";
import { FaFacebookF, FaInstagram } from "react-icons/fa";
import { formatCep } from "@/lib/cep";
import type { AcceptedCardBrand } from "@/types/company";
import type { PublicCompany } from "@/types/store";
import styles from "./retail.module.css";

const CARD_PAYMENT_IMAGES: Record<
  AcceptedCardBrand,
  { src: string; alt: string; width: number; height: number }
> = {
  visa: { src: "/payments/visa.png", alt: "Visa", width: 294, height: 99 },
  mastercard: {
    src: "/payments/mastercard.png",
    alt: "Mastercard",
    width: 485,
    height: 378,
  },
  elo: { src: "/payments/elo.png", alt: "Elo", width: 3500, height: 1339 },
  amex: {
    src: "/payments/American-Express.png",
    alt: "American Express",
    width: 1270,
    height: 375,
  },
  hipercard: {
    src: "/payments/hipercard.png",
    alt: "Hipercard",
    width: 294,
    height: 131,
  },
};

type Props = {
  company: PublicCompany;
  formattedCnpj: string;
  instagramUrl: string;
  facebookUrl: string;
  onHome: () => void;
  onCatalog: () => void;
};

export default function RetailFooter({
  company,
  formattedCnpj,
  instagramUrl,
  facebookUrl,
  onHome,
  onCatalog,
}: Props) {
  const clean = (value: string) => value.trim().replace(/\s+/g, " ");
  const addressFirstLine = [
    [company.address, company.number].map(clean).filter(Boolean).join(", "),
    clean(company.district),
  ]
    .filter(Boolean)
    .join(" - ");
  const cep = formatCep(company.cep);
  const addressSecondLine = [
    cep && `CEP ${cep}`,
    [company.city, company.state].map(clean).filter(Boolean).join(" - "),
  ]
    .filter(Boolean)
    .join(" • ");
  const socials = [
    { label: "Instagram", url: instagramUrl.trim(), Icon: FaInstagram },
    { label: "Facebook", url: facebookUrl.trim(), Icon: FaFacebookF },
  ].filter(({ url }) => /^https?:\/\//i.test(url));
  const hasInformation =
    company.pickup_enabled ||
    company.local_delivery_enabled ||
    company.accept_pix ||
    company.accept_card_on_delivery ||
    company.accept_cash_on_delivery;
  return (
    <footer className={styles.footer} id="contato">
      <div
        className={`${styles.container} ${styles.footerColumns} ${socials.length ? "" : styles.footerColumnsWithoutSocials}`}
      >
        <div className={styles.footerIdentity} id="sobre">
          <div className={styles.identity}>
            {company.logo_url && (
              <img
                src={company.logo_url}
                alt=""
                loading="lazy"
                decoding="async"
              />
            )}
            <p>{company.name}</p>
          </div>
          {(addressFirstLine || addressSecondLine) && (
            <address className={styles.contactLine}>
              <MapPin size={17} aria-hidden="true" />
              <span className={styles.addressLines}>
                {addressFirstLine && <span>{addressFirstLine}</span>}
                {addressSecondLine && <span>{addressSecondLine}</span>}
              </span>
            </address>
          )}
          {company.email && (
            <a className={styles.contactLine} href={`mailto:${company.email}`}>
              <Mail size={16} aria-hidden="true" />
              <span>{company.email}</span>
            </a>
          )}
        </div>
        <nav aria-label="Navegação do rodapé">
          <h2>Institucional</h2>
          <a href="#inicio" onClick={onHome}>
            Início
          </a>
          <a href="#catalogo" onClick={onCatalog}>
            Produtos
          </a>
          {company.description && <a href="#sobre">Sobre a empresa</a>}
          <a href="#contato">Contato</a>
        </nav>
        {hasInformation && (
          <div id="informacoes">
            <h2>Informações da loja</h2>
            {company.local_delivery_enabled && <p>Entrega local disponível</p>}
            {company.pickup_enabled && <p>Retirada na loja</p>}
            {company.accept_pix && <p>Pagamento via Pix</p>}
            {company.accept_card_on_delivery && (
              <p>Cartão na entrega ou retirada</p>
            )}
            {company.accept_cash_on_delivery && (
              <p>Dinheiro na entrega ou retirada</p>
            )}
          </div>
        )}
        {socials.length > 0 && (
          <div>
            <h2>Redes sociais</h2>
            <nav aria-label="Redes sociais" className={styles.socials}>
              {socials.map(({ label, url, Icon }) => (
                <a
                  key={label}
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={`Visitar ${label} da empresa`}
                >
                  <Icon size={18} aria-hidden="true" />
                </a>
              ))}
            </nav>
          </div>
        )}
      </div>
      {(company.accept_pix ||
        company.accept_card_on_delivery ||
        company.accept_cash_on_delivery) && (
        <div className={`${styles.container} ${styles.footerPayments}`}>
          <p className={styles.paymentTitle}>Formas de pagamento</p>
          <div className={styles.payments} aria-label="Formas de pagamento">
            {company.accept_pix && (
              <span className={styles.paymentCard}>
                <Image
                  src="/payments/pix.png"
                  alt="Pix"
                  width={954}
                  height={339}
                  sizes="40px"
                  className={styles.paymentLogo}
                />
              </span>
            )}
            {company.accept_card_on_delivery &&
              (company.accepted_card_brands.length ? (
                company.accepted_card_brands.map((brand) => {
                  const payment = Object.hasOwn(CARD_PAYMENT_IMAGES, brand)
                    ? CARD_PAYMENT_IMAGES[brand]
                    : undefined;
                  return payment ? (
                    <span key={brand} className={styles.paymentCard}>
                      <Image
                        {...payment}
                        sizes="40px"
                        className={styles.paymentLogo}
                      />
                    </span>
                  ) : null;
                })
              ) : (
                <span
                  className={`${styles.paymentCard} ${styles.paymentFallback}`}
                >
                  Cartão
                </span>
              ))}
            {company.accept_cash_on_delivery && (
              <span
                className={`${styles.paymentCard} ${styles.paymentFallback}`}
              >
                Dinheiro
              </span>
            )}
          </div>
        </div>
      )}
      <div className={styles.footerBottom}>
        <div className={`${styles.container} ${styles.footerLegal}`}>
          <div>
            <p>
              <span className={styles.legalCompany}>
                © {new Date().getFullYear()} {company.name}
                {formattedCnpj && <> • CNPJ {formattedCnpj}</>}
              </span>{" "}
              <span className={styles.legalRights}>
                <span className={styles.legalSeparator}>• </span>
                Todos os direitos reservados.
              </span>
            </p>
            {company.legal_name && company.legal_name !== company.name && (
              <p className={styles.legalName}>{company.legal_name}</p>
            )}
          </div>
          <p className={styles.attribution}>
            <span>Desenvolvido por</span>{" "}
            <span className={styles.platformIcon} aria-hidden="true">
              <Image
                src="/images/logo-icon-fornecedor.png"
                alt=""
                width={713}
                height={779}
                sizes="14px"
                className={styles.platformLogo}
              />
            </span>{" "}
            <a href="https://fornecedor.net" target="_blank" rel="noreferrer">
              Fornecedor.Net
            </a>
          </p>
        </div>
      </div>
    </footer>
  );
}
